import { useSelector } from "react-redux";
import { getUserDetailsFromReducer } from "Store/reducers/Auth/AuthSlice";
import {
  hasFeatureAccess,
  getUserAccessibleFeatures,
  getPremiumFeatures,
  getComingSoonFeatures,
  checkUsageLimit,
  getUsageLimits,
} from "Utility/featureUtils";

/**
 * Custom hook for feature access management
 * Supports plan-based (plan field) and legacy (userType only) accounts.
 * @returns {Object} Feature access utilities and user data
 */
export const useFeatureAccess = () => {
  const userData = useSelector(getUserDetailsFromReducer);
  const userFeatures = userData?.paidWidgets || [];
  const plan = userData?.plan;
  const userType = userData?.userType;
  const isPaidUser = userData?.isPaidUser; // Legacy check

  const isTrialValid = () => {
    if (userType === "trial") {
      const nextBillingAt = userData?.nextBillingAt;
      if (!nextBillingAt) return false;
      return new Date(nextBillingAt) > new Date();
    }
    return false;
  };

  const hasFullAccess =
    userType === "paid" || userType === "lifetime" || isPaidUser === true;

  // Effective tier: when plan is set use it; when plan is null (legacy) use userType
  const isFreeTier = () => {
    if (plan != null && plan !== undefined) return plan === "free";
    return userType === "free" || (userType === "trial" && !isTrialValid());
  };

  const isPaidTier = () => {
    if (plan != null && plan !== undefined)
      return plan === "essential" || plan === "pro";
    return hasFullAccess || (userType === "trial" && isTrialValid());
  };

  let effectiveUserFeatures = [...userFeatures];

  if (isFreeTier()) {
    effectiveUserFeatures = [];
  }

  if (isPaidTier()) {
    if (!effectiveUserFeatures.includes("review-generator"))
      effectiveUserFeatures.push("review-generator");
    if (!effectiveUserFeatures.includes("seo")) effectiveUserFeatures.push("seo");
  }

  /**
   * Check if user has access to a specific feature
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} - Whether the user has access to the feature
   */
  const hasAccess = (featureKey) => {
    return hasFeatureAccess(featureKey, effectiveUserFeatures);
  };

  /**
   * Get all features the user can access
   * @returns {Array} - Array of feature objects the user can access
   */
  const getAccessibleFeatures = () => {
    return getUserAccessibleFeatures(effectiveUserFeatures);
  };

  /**
   * Get premium features that require purchase
   * @returns {Array} - Array of premium feature objects
   */
  const getPremiumFeaturesList = () => {
    return getPremiumFeatures();
  };

  /**
   * Get coming soon features
   * @returns {Array} - Array of coming soon feature objects
   */
  const getComingSoonFeaturesList = () => {
    return getComingSoonFeatures();
  };

  /**
   * Get user's purchased features
   * @returns {Array} - Array of feature keys the user has purchased
   */
  const getPurchasedFeatures = () => {
    return effectiveUserFeatures;
  };

  /**
   * Check if user has any premium features
   * @returns {boolean} - Whether the user has any premium features
   */
  const hasAnyPremiumFeatures = () => {
    return effectiveUserFeatures.length > 0;
  };

  const usage = {
    feedbackViews: userData?.usageFeedbackViews ?? 0,
    aiReviews: userData?.usageAiReviews ?? 0,
    extensionResponses: userData?.usageExtensionResponses ?? 0,
    periodEndAt: userData?.usagePeriodEndAt,
  };

  const effectivePlan = plan != null ? plan : (userType === "free" ? "free" : userType === "trial" ? (isTrialValid() ? "trial" : "free") : "paid");

  const getUsage = () => ({ ...usage });
  const getUsageLimitsForPlan = () => getUsageLimits(effectivePlan === "trial" || effectivePlan === "paid" ? "pro" : effectivePlan);

  const hasUsageLeft = (featureKey) => {
    if (!isFreeTier()) return true;
    const result = checkUsageLimit("free", usage, featureKey);
    return result.allowed;
  };

  const getUsageForFeature = (featureKey) => {
    if (!isFreeTier()) return { allowed: true, used: 0, limit: null, remaining: null };
    return checkUsageLimit("free", usage, featureKey);
  };

  const recordUsage = async (featureKey) => {
    if (!["feedbackViews", "aiReviews", "extensionResponses"].includes(featureKey)) return;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!apiBase) return;
    try {
      const { Auth } = await import("aws-amplify");
      const session = await Auth.currentSession();
      const token = session?.getIdToken?.()?.getJwtToken?.();
      await fetch(`${apiBase}/api/usage/record`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ featureKey }),
      });
    } catch (e) {
      console.warn("recordUsage failed:", e);
    }
  };

  return {
    userData,
    userFeatures: effectiveUserFeatures,
    plan: effectivePlan,
    hasAccess,
    getAccessibleFeatures,
    getPremiumFeaturesList,
    getComingSoonFeaturesList,
    getPurchasedFeatures,
    hasAnyPremiumFeatures,
    isTrialValid: isTrialValid(),
    nextBillingAt: userData?.nextBillingAt,
    userType,
    getUsage,
    getUsageLimitsForPlan,
    hasUsageLeft,
    getUsageForFeature,
    recordUsage,
  };
};
